// Audio pipeline for the widget — mirrors components/iva/TestConsole/useTestCall.ts
// but inlined so the public bundle has zero external worklet dependencies.
//
//   - Mic capture → AudioWorklet `pcm-capture` (Int16 frames)
//   - Down-resample to 24 kHz mono PCM16 (Azure OpenAI Realtime input format)
//   - Accumulate ~80ms frames, hand back to caller as base64 strings
//   - Agent audio is pushed into `pcm-playback` for playback
//
// The worklet processor source is held as a literal string and registered
// via a Blob URL so consumers don't need to ship a separate worklet file.

const WORKLET_SRC = `
class PcmCapture extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0]) return true;
    const ch = input[0];
    const out = new Int16Array(ch.length);
    for (let i = 0; i < ch.length; i++) {
      let s = ch[i];
      if (s > 1) s = 1; else if (s < -1) s = -1;
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    this.port.postMessage({ type: 'pcm', buffer: out }, [out.buffer]);
    return true;
  }
}
class PcmPlayback extends AudioWorkletProcessor {
  constructor() {
    super();
    this.queue = [];
    this.cursor = 0;
    this.active = false;
    this.levelTick = 0;
    this.port.onmessage = (e) => {
      const d = e.data;
      if (!d) return;
      if (d.type === 'clear') { this.queue = []; this.cursor = 0; this.active = false; return; }
      if (d.type === 'pcm' && d.buffer && d.buffer.length) {
        const int16 = d.buffer;
        const f = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) {
          const v = int16[i];
          f[i] = v < 0 ? v / 0x8000 : v / 0x7fff;
        }
        this.queue.push(f);
        this.active = true;
      }
    };
  }
  process(_in, outputs) {
    const out = outputs[0];
    if (!out || !out[0]) return true;
    const ch = out[0];
    let i = 0;
    let sumSquares = 0;
    while (i < ch.length) {
      if (!this.queue.length) { for (; i < ch.length; i++) ch[i] = 0; break; }
      const head = this.queue[0];
      const remaining = head.length - this.cursor;
      const take = Math.min(remaining, ch.length - i);
      for (let k = 0; k < take; k++) {
        const sample = head[this.cursor + k];
        ch[i + k] = sample;
        sumSquares += sample * sample;
      }
      this.cursor += take;
      i += take;
      if (this.cursor >= head.length) { this.queue.shift(); this.cursor = 0; }
    }
    if (++this.levelTick >= 6) {
      this.levelTick = 0;
      this.port.postMessage({ type: 'level', value: Math.min(1, Math.sqrt(sumSquares / Math.max(1, ch.length)) * 3.5) });
    }
    // Notify the main thread once the queue empties after having played audio,
    // so the controller knows the agent has finished verbalizing locally.
    if (this.active && this.queue.length === 0) {
      this.active = false;
      this.port.postMessage({ type: 'level', value: 0 });
      this.port.postMessage({ type: 'drained' });
    }
    return true;
  }
}
registerProcessor('pcm-capture', PcmCapture);
registerProcessor('pcm-playback', PcmPlayback);
`;

/** Read a Blob into a bare base64 string (no data: URI prefix). */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('blob_read_failed'));
    reader.readAsDataURL(blob);
  });
}

const TARGET_SAMPLE_RATE = 24000;
const FRAME_FLUSH_MS = 80;

/**
 * Build a playable `audio/wav` object URL from a list of base64-encoded PCM16LE
 * mono chunks (the same format the realtime bridge streams as `audio.delta`,
 * and that the capture pipeline emits per frame). Returns null when there is no
 * usable audio. The caller owns the returned URL and may revoke it.
 */
export function pcm16Base64ChunksToWavUrl(chunks: string[], sampleRate = TARGET_SAMPLE_RATE): string | null {
  if (!chunks || !chunks.length) return null;
  const parts: Uint8Array[] = [];
  let total = 0;
  for (const b64 of chunks) {
    if (!b64) continue;
    try {
      const bin = atob(b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      parts.push(arr);
      total += arr.length;
    } catch { /* skip bad chunk */ }
  }
  if (total < 2) return null;
  const pcm = new Uint8Array(total);
  let off = 0;
  for (const p of parts) { pcm.set(p, off); off += p.length; }

  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const writeStr = (pos: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(pos + i, s.charCodeAt(i)); };
  const dataLen = pcm.length;
  const blockAlign = 2; // mono * 16-bit
  const byteRate = sampleRate * blockAlign;
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + dataLen, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);       // PCM fmt chunk size
  view.setUint16(20, 1, true);        // audio format = PCM
  view.setUint16(22, 1, true);        // channels = mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);       // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, dataLen, true);

  const out = new Uint8Array(44 + dataLen);
  out.set(new Uint8Array(header), 0);
  out.set(pcm, 44);
  try { return URL.createObjectURL(new Blob([out], { type: 'audio/wav' })); }
  catch { return null; }
}

export type AudioFrameHandler = (base64: string) => void;

export class WidgetAudio {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private capture: AudioWorkletNode | null = null;
  private playback: AudioWorkletNode | null = null;
  private workletUrl: string | null = null;

  private pcmBuf: Float32Array[] = [];
  private pcmLen = 0;
  private flushTimer: number | null = null;
  private muted = false;

  // Push-to-talk (near-real-time) capture. Independent of the continuous
  // streaming worklet pipeline used by the realtime path — the near-RT path
  // records a single utterance and submits it as an audio file for STT.
  private recorder: any = null;
  private recStream: MediaStream | null = null;
  private recChunks: BlobPart[] = [];
  private recording = false;

  private onFrame: AudioFrameHandler = () => {};
  private onDrained: () => void = () => {};
  private onPlaybackLevel: (level: number) => void = () => {};
  private onCaptureMetrics: (samples: number, rms: number) => void = () => {};

  setOnFrame(fn: AudioFrameHandler): void { this.onFrame = fn; }
  /** Fired (main thread) when agent playback finishes and the queue drains. */
  setOnDrained(fn: () => void): void { this.onDrained = fn; }
  setOnPlaybackLevel(fn: (level: number) => void): void { this.onPlaybackLevel = fn; }
  setOnCaptureMetrics(fn: (samples: number, rms: number) => void): void { this.onCaptureMetrics = fn; }
  setMuted(value: boolean): void { this.muted = value; }
  isMuted(): boolean { return this.muted; }
  isRecording(): boolean { return this.recording; }

  private static pickRecorderMime(): string {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
    const MR: any = (window as any).MediaRecorder;
    if (MR && typeof MR.isTypeSupported === 'function') {
      for (const c of candidates) {
        try { if (MR.isTypeSupported(c)) return c; } catch { /* */ }
      }
    }
    return '';
  }

  /** Begin recording a single push-to-talk utterance (near-RT voice). */
  async startUtterance(): Promise<void> {
    if (this.recording) return;
    const MR: any = (window as any).MediaRecorder;
    if (!MR) throw new Error('recording_unsupported');
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    this.recStream = stream;
    this.recChunks = [];
    const mimeType = WidgetAudio.pickRecorderMime();
    const rec = mimeType ? new MR(stream, { mimeType }) : new MR(stream);
    rec.ondataavailable = (e: any) => { if (e?.data && e.data.size) this.recChunks.push(e.data); };
    this.recorder = rec;
    this.recording = true;
    rec.start();
  }

  /**
   * Stop the active utterance and return it as base64 + mimeType, or null when
   * nothing usable was captured.
   */
  async stopUtterance(): Promise<{ base64: string; mimeType: string } | null> {
    const rec = this.recorder;
    if (!rec || !this.recording) { this.cleanupRecorder(); return null; }
    const mimeType = rec.mimeType || 'audio/webm';
    const done = new Promise<void>((resolve) => { rec.onstop = () => resolve(); });
    try { rec.stop(); } catch { /* */ }
    await done;
    this.recording = false;
    const chunks = this.recChunks;
    this.cleanupRecorder();
    if (!chunks.length) return null;
    const blob = new Blob(chunks, { type: mimeType });
    if (!blob.size) return null;
    const base64 = await blobToBase64(blob);
    if (!base64) return null;
    return { base64, mimeType };
  }

  /** Abort any in-flight utterance recording without returning audio. */
  cancelUtterance(): void {
    const rec = this.recorder;
    if (rec && this.recording) { try { rec.stop(); } catch { /* */ } }
    this.recording = false;
    this.cleanupRecorder();
  }

  private cleanupRecorder(): void {
    try { this.recStream?.getTracks().forEach(t => t.stop()); } catch { /* */ }
    this.recorder = null;
    this.recStream = null;
    this.recChunks = [];
  }

  async start(): Promise<void> {
    if (this.ctx) return;
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    this.stream = stream;

    let ctx: AudioContext;
    try { ctx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE }); }
    catch { ctx = new AudioContext(); }
    this.ctx = ctx;

    const blob = new Blob([WORKLET_SRC], { type: 'application/javascript' });
    this.workletUrl = URL.createObjectURL(blob);
    await ctx.audioWorklet.addModule(this.workletUrl);

    this.source = ctx.createMediaStreamSource(stream);
    this.capture = new AudioWorkletNode(ctx, 'pcm-capture');
    this.playback = new AudioWorkletNode(ctx, 'pcm-playback');
    this.playback.port.onmessage = (event: MessageEvent) => {
      const data = event.data as any;
      if (data?.type === 'level') {
        try { this.onPlaybackLevel(Number(data.value) || 0); } catch { /* host handles */ }
      } else if (data?.type === 'drained') {
        try { this.onDrained(); } catch { /* host handles */ }
      }
    };

    this.capture.port.onmessage = (event: MessageEvent) => {
      if (this.muted) return;
      const data: any = event.data;
      if (!data || data.type !== 'pcm') return;
      const int16: Int16Array = data.buffer;
      let sumSquares = 0;
      // Convert to Float32 for resample pass.
      const f = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) {
        const v = int16[i];
        f[i] = v < 0 ? v / 0x8000 : v / 0x7fff;
        sumSquares += f[i] * f[i];
      }
      try { this.onCaptureMetrics(int16.length, Math.sqrt(sumSquares / Math.max(1, int16.length))); } catch { /* host handles */ }
      this.pcmBuf.push(f);
      this.pcmLen += f.length;
    };

    this.source.connect(this.capture);
    this.playback.connect(ctx.destination);

    this.flushTimer = window.setInterval(() => { this.flush().catch(() => {}); }, FRAME_FLUSH_MS);
  }

  private async flush(): Promise<void> {
    if (this.pcmLen === 0 || !this.ctx) return;
    const merged = new Float32Array(this.pcmLen);
    let offset = 0;
    for (const c of this.pcmBuf) { merged.set(c, offset); offset += c.length; }
    this.pcmBuf = [];
    this.pcmLen = 0;

    const srcRate = this.ctx.sampleRate;
    const targetLen = Math.floor((merged.length * TARGET_SAMPLE_RATE) / srcRate);
    const resampled = new Int16Array(targetLen);
    for (let i = 0; i < targetLen; i++) {
      const srcIndex = (i * srcRate) / TARGET_SAMPLE_RATE;
      const i0 = Math.floor(srcIndex);
      const i1 = Math.min(i0 + 1, merged.length - 1);
      const frac = srcIndex - i0;
      let s = merged[i0] * (1 - frac) + merged[i1] * frac;
      if (s > 1) s = 1; else if (s < -1) s = -1;
      resampled[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }

    const bytes = new Uint8Array(resampled.buffer);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)) as any);
    }
    const b64 = btoa(binary);
    try { this.onFrame(b64); } catch { /* host handles */ }
  }

  /** Force-flush any buffered capture audio immediately (e.g. before a commit). */
  async flushPending(): Promise<void> {
    await this.flush();
  }

  /** Push an inbound base64-encoded PCM16 buffer into the playback queue. */
  enqueuePlayback(b64: string): void {
    const node = this.playback;
    if (!node || !b64) return;
    try {
      const binary = atob(b64);
      const buf = new Int16Array(binary.length / 2);
      for (let i = 0; i < buf.length; i++) {
        let v = binary.charCodeAt(i * 2) | (binary.charCodeAt(i * 2 + 1) << 8);
        if (v >= 0x8000) v -= 0x10000;
        buf[i] = v;
      }
      node.port.postMessage({ type: 'pcm', buffer: buf }, [buf.buffer]);
    } catch { /* drop bad frame */ }
  }

  clearPlayback(): void {
    try { this.playback?.port.postMessage({ type: 'clear' }); } catch { /* */ }
    try { this.onPlaybackLevel(0); } catch { /* host handles */ }
  }

  async stop(): Promise<void> {
    this.cancelUtterance();
    if (this.flushTimer) { window.clearInterval(this.flushTimer); this.flushTimer = null; }
    try { this.capture?.disconnect(); } catch { /* */ }
    try { this.playback?.disconnect(); } catch { /* */ }
    try { this.source?.disconnect(); } catch { /* */ }
    try { this.stream?.getTracks().forEach(t => t.stop()); } catch { /* */ }
    this.capture = null;
    this.playback = null;
    this.source = null;
    this.stream = null;
    this.pcmBuf = [];
    this.pcmLen = 0;
    try { this.onPlaybackLevel(0); } catch { /* host handles */ }
    if (this.ctx) {
      try { await this.ctx.close(); } catch { /* */ }
      this.ctx = null;
    }
    if (this.workletUrl) {
      try { URL.revokeObjectURL(this.workletUrl); } catch { /* */ }
      this.workletUrl = null;
    }
  }
}
